import { addDays } from '@shh/shared';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useLens } from '../../app/lens';
import { addMonths, ALL_LAYERS, LAYERS, weekStart, type Layers } from '../../data/calendar';
import { useFamily } from '../../data/family';

/**
 * Which view and day the calendar shows (1.24). Both live in the address
 * (`/kalender?ansicht=woche&datum=2026-10-19`), so "Tagesplan →" on Heute, the week number in
 * the month and the back button all work. Without an address the device's last view opens –
 * the wall shows the week by person (Mike, 18.10.).
 */
export type CalView = 'tag' | 'woche' | 'monat' | 'jahr';
export const VIEWS: CalView[] = ['tag', 'woche', 'monat', 'jahr'];

const VIEW_KEY = 'shh:calView';
const LAYER_KEY = 'shh:calLayers';

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode – the default view is fine
  }
};

export function useCalendarView() {
  const f = useFamily();
  const lens = useLens();
  const [params, setParams] = useSearchParams();
  const asked = params.get('ansicht') as CalView | null;
  const stored = read(VIEW_KEY) as CalView | null;
  // phones start with the month (and its day list), big screens with the week by person
  const wide = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches;
  const fallback: CalView = lens.isWall ? 'woche' : (stored ?? (wide ? 'woche' : 'monat'));
  const view: CalView = asked && VIEWS.includes(asked) ? asked : fallback;
  const datum = params.get('datum');
  const date = datum && /^\d{4}-\d{2}-\d{2}$/.test(datum) ? datum : f.today;

  const go = useCallback(
    (next: { view?: CalView; date?: string }, push = false) => {
      const p = new URLSearchParams(params);
      if (next.view) {
        p.set('ansicht', next.view);
        if (!lens.isWall) write(VIEW_KEY, next.view);
      }
      if (next.date) {
        if (next.date === f.today) p.delete('datum');
        else p.set('datum', next.date);
      }
      // another day in the same view keeps the scroll position – the app otherwise starts every
      // new address at the top, and the month's list jumped back up (Mike, 19.10.: "Die Liste
      // springt nicht zu dem gewählten Tag")
      setParams(p, { replace: !push, preventScrollReset: !next.view });
    },
    [params, setParams, f.today, lens.isWall],
  );

  /** One step back or forward in the current view. */
  const step = useCallback(
    (dir: 1 | -1, unit?: 'day' | 'week' | 'month') => {
      const u =
        unit ??
        (view === 'tag' ? 'day' : view === 'woche' ? 'week' : view === 'monat' ? 'month' : 'year');
      const next =
        u === 'day'
          ? addDays(date, dir)
          : u === 'week'
            ? addDays(date, 7 * dir)
            : u === 'month'
              ? addMonths(date, dir)
              : addMonths(date, 12 * dir);
      go({ date: next });
    },
    [view, date, go],
  );

  // layers per device ("Was steht im Kalender?")
  const [layers, setLayersState] = useState<Layers>(() => {
    try {
      const saved = JSON.parse(read(LAYER_KEY) ?? '{}') as Partial<Layers>;
      return {
        ...ALL_LAYERS,
        ...Object.fromEntries(
          LAYERS.filter((l) => typeof saved[l] === 'boolean').map((l) => [l, saved[l]]),
        ),
      };
    } catch {
      return ALL_LAYERS;
    }
  });
  const setLayer = useCallback((layer: keyof Layers, on: boolean) => {
    setLayersState((old) => {
      const next = { ...old, [layer]: on };
      write(LAYER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Mike (18.10.): in the calendar the person chip hides the others; Heute only dims them
  const people = useMemo(() => (lens.id === 'all' ? undefined : [lens.id]), [lens.id]);

  return { view, date, go, step, layers, setLayer, people, weekOf: weekStart(date) };
}
